// Learning Platform Type Definitions

export interface LearningModule {
  id: string
  title: string
  description?: string
  difficulty: 'beginner' | 'intermediate' | 'advanced'
  order_index: number
  icon?: string
  is_published: boolean
  created_at: string
  updated_at: string
  // Computed fields
  videos?: LearningVideo[]
  progress?: ModuleProgress
}

export interface LearningVideo {
  id: string
  module_id: string
  title: string
  description?: string
  video_url: string
  video_embed_id: string
  duration?: number // seconds
  difficulty: 'beginner' | 'intermediate' | 'advanced'
  category: 'monetary-policy' | 'banking' | 'personal-finance' | 'investments' | 'economic-indicators'
  source: 'federal-reserve' | 'cfpb' | 'sec' | 'treasury' | 'other'
  learning_objectives: string[]
  prerequisites: string[] // video IDs
  tags: string[]
  thumbnail_url?: string
  transcript?: string
  xp_reward: number
  order_index: number
  is_published: boolean
  created_at: string
  updated_at: string
  // Computed fields
  module?: LearningModule
  progress?: VideoProgress
  quiz_questions?: QuizQuestion[]
}

export interface QuizQuestion {
  id: string
  video_id: string
  question_text: string
  question_type: 'multiple-choice' | 'true-false' | 'short-answer'
  options?: string[] // For multiple choice questions
  correct_answer: string
  explanation?: string
  difficulty: 'easy' | 'medium' | 'hard'
  order_index: number
  created_at: string
}

export interface VideoProgress {
  id: string
  user_id: string
  video_id: string
  watched_seconds: number
  completed: boolean
  completed_at?: string
  last_watched_at: string
  // Computed fields
  completion_percentage: number
  time_remaining: number
}

export interface QuizAttempt {
  id: string
  user_id: string
  video_id: string
  score: number
  total_questions: number
  answers: QuizAnswer[]
  xp_earned: number
  completed_at: string
}

export interface QuizAnswer {
  question_id: string
  selected_answer: string
  is_correct: boolean
  question?: QuizQuestion
}

export interface QuizResult {
  score: number
  total_questions: number
  percentage: number
  xp_earned: number
  answers: QuizAnswer[]
  perfect_score: boolean
  can_retake: boolean
}

export interface LearningAchievement {
  id: string
  name: string
  description?: string
  icon?: string
  type: 'first-video' | 'quiz-perfect' | 'streak' | 'module-complete' | 'milestone'
  requirement: AchievementRequirement
  xp_reward: number
  created_at: string
  // Computed fields
  is_earned?: boolean
  earned_at?: string
  progress?: AchievementProgress
}

export interface AchievementRequirement {
  type: 'videos_watched' | 'perfect_quizzes' | 'daily_streak' | 'modules_completed' | 'fed_videos_watched' | 'all_modules_completed'
  count: number
  category?: string
  source?: string
}

export interface AchievementProgress {
  current: number
  required: number
  percentage: number
  is_complete: boolean
}

export interface UserLearningAchievement {
  id: string
  user_id: string
  achievement_id: string
  earned_at: string
  achievement?: LearningAchievement
}

export interface UserLearningProgress {
  user_id: string
  total_videos_watched: number
  total_xp_earned: number
  current_streak: number
  longest_streak: number
  modules_completed: number
  perfect_quiz_count: number
  achievements_earned: number
  last_activity_date?: string
  // Computed fields
  level: number
  xp_to_next_level: number
  recent_achievements: LearningAchievement[]
  next_recommended_video?: LearningVideo
  continue_learning_video?: LearningVideo
}

export interface ModuleProgress {
  module_id: string
  user_id: string
  videos_completed: number
  total_videos: number
  completion_percentage: number
  is_completed: boolean
  completed_at?: string
  xp_earned: number
}

export interface LearningStats {
  total_modules: number
  total_videos: number
  total_questions: number
  total_achievements: number
  average_video_duration: number
  most_popular_category: string
  completion_rate: number
}

export interface VideoRecommendation {
  video: LearningVideo
  reason: 'continue_learning' | 'prerequisite_met' | 'similar_content' | 'popular' | 'new'
  confidence: number
}

export interface LearningPath {
  id: string
  name: string
  description: string
  difficulty: 'beginner' | 'intermediate' | 'advanced'
  modules: LearningModule[]
  estimated_hours: number
  prerequisites?: string[]
  learning_objectives: string[]
}

// API Request/Response Types
export interface CreateVideoRequest {
  module_id: string
  title: string
  description?: string
  video_url: string
  difficulty: 'beginner' | 'intermediate' | 'advanced'
  category: 'monetary-policy' | 'banking' | 'personal-finance' | 'investments' | 'economic-indicators'
  source: 'federal-reserve' | 'cfpb' | 'sec' | 'treasury' | 'other'
  learning_objectives: string[]
  prerequisites?: string[]
  tags?: string[]
  xp_reward?: number
  order_index: number
}

export interface UpdateVideoProgressRequest {
  video_id: string
  watched_seconds: number
  completed?: boolean
}

export interface SubmitQuizRequest {
  video_id: string
  answers: {
    question_id: string
    selected_answer: string
  }[]
}

export interface GenerateQuizRequest {
  video_id: string
  video_info: {
    title: string
    description?: string
    transcript?: string
    learning_objectives: string[]
    category: string
    difficulty: string
  }
}

// Error Types
export interface LearningError {
  code: string
  message: string
  details?: any
}

export interface ValidationError {
  field: string
  message: string
  value?: any
}

// Utility Types
export type LearningDifficulty = 'beginner' | 'intermediate' | 'advanced'
export type VideoCategory = 'monetary-policy' | 'banking' | 'personal-finance' | 'investments' | 'economic-indicators'
export type VideoSource = 'federal-reserve' | 'cfpb' | 'sec' | 'treasury' | 'other'
export type QuestionType = 'multiple-choice' | 'true-false' | 'short-answer'
export type QuestionDifficulty = 'easy' | 'medium' | 'hard'
export type AchievementType = 'first-video' | 'quiz-perfect' | 'streak' | 'module-complete' | 'milestone'

// Constants
export const XP_REWARDS = {
  VIDEO_COMPLETION: {
    BEGINNER: 50,
    INTERMEDIATE: 75,
    ADVANCED: 100
  },
  QUIZ_PERFECT: {
    EASY: 25,
    MEDIUM: 35,
    HARD: 50
  },
  DAILY_STREAK: 10,
  MODULE_COMPLETION: {
    SMALL: 200, // 1-3 videos
    MEDIUM: 350, // 4-6 videos
    LARGE: 500  // 7+ videos
  }
} as const

export const LEVEL_THRESHOLDS = [
  0,    // Level 1
  500,  // Level 2
  1500, // Level 3
  3000, // Level 4
  5000, // Level 5
  7500, // Level 6
  10000, // Level 7
  13000, // Level 8
  16000, // Level 9
  20000  // Level 10
] as const

export const VIDEO_CATEGORIES = {
  'monetary-policy': 'Monetary Policy',
  'banking': 'Banking & Finance',
  'personal-finance': 'Personal Finance',
  'investments': 'Investments',
  'economic-indicators': 'Economic Indicators'
} as const

export const VIDEO_SOURCES = {
  'federal-reserve': 'Federal Reserve',
  'cfpb': 'Consumer Financial Protection Bureau',
  'sec': 'Securities and Exchange Commission',
  'treasury': 'Treasury Department',
  'other': 'Other Government Source'
} as const
